import React, { useState, useMemo } from 'react';
import { ChevronDown, Share2, User } from 'lucide-react';
import { SocialIcon } from '../icons/SocialIcon';
import { useSocialPlatforms } from '../../hooks/useSocialPlatforms';
import { SocialProfilePicture } from '../common/SocialProfilePicture';
import type { SocialPlatform } from '../icons/SocialIcon';
import { useTranslation } from 'react-i18next';
import { SOCIAL_PLATFORMS, Platform } from '../../constants/SocialPlatforms';
import { BrandDropdown } from '../common/BrandDropdown';
import type { Brand } from '../../types';
import { useBrands } from '../../hooks/useBrands';

interface SocialAccount {
  platform: SocialPlatform;
  username: string;
  picture_url?: string;
}

interface BrandsAndPlatformsCardProps {
  selectedBrandId: string;
  onBrandSelect: (brandId: string) => void;
  selectedPlatforms: SocialPlatform[];
  onPlatformToggle: (platform: SocialPlatform) => void;
  onFormatChange?: (format: 'regular' | 'reel') => void;
  format?: 'text' | 'reel' | 'regular';
  connectedAccounts?: SocialAccount[];
  tiktokAccount?: {
    username: string;
    picture_url?: string;
  };
}

export const BrandsAndPlatformsCard: React.FC<BrandsAndPlatformsCardProps> = ({
  selectedBrandId,
  onBrandSelect,
  selectedPlatforms,
  onPlatformToggle,
  onFormatChange,
  format = 'text',
  connectedAccounts = [],
  tiktokAccount
}) => {
  const { t } = useTranslation();
  const { availablePlatforms } = useSocialPlatforms();

  const { data: brandsData, isLoading: isLoadingBrands } = useBrands();
  const brands = (brandsData as any)?.brands || [];

  const allConnectedAccounts = useMemo(() => {
    const accounts = [...connectedAccounts];

    if (tiktokAccount?.username && !accounts.some(acc => acc.platform === 'tiktok')) {
      accounts.push({
        platform: 'tiktok',
        username: tiktokAccount.username,
        picture_url: tiktokAccount.picture_url
      });
    }

    return accounts;
  }, [connectedAccounts, tiktokAccount]);

  const handlePlatformToggle = (platform: SocialPlatform) => {
    onPlatformToggle(platform);

    if ((platform === 'youtube' || platform === 'tiktok') && onFormatChange) {
      if (!selectedPlatforms.includes(platform)) {
        onFormatChange('reel');
      }
    }
  };

  const isPlatformDisabled = (platformId: SocialPlatform) => {
    if (format === 'regular') {
      return platformId === 'tiktok' || platformId === 'youtube';
    }
    if (format === 'reel') {
      return platformId === 'linkedin' || platformId === 'pinterest' || platformId === 'twitter';
    }
    return false;
  };

  const displayPlatforms = [...availablePlatforms,
     ...SOCIAL_PLATFORMS
       .filter((p: Platform) => (p.id === 'facebook' || p.id === 'instagram') && p.status === 'coming-soon')
    ];

  const getConnectedAccount = (platformId: SocialPlatform) => {
    return allConnectedAccounts.find(account => account.platform === platformId);
  };

  const renderPlatformButton = (platform: { id: SocialPlatform; name: string }) => {
    const isSelected = selectedPlatforms.includes(platform.id);
    const connectedAccount = getConnectedAccount(platform.id);
    const isDisabled = isPlatformDisabled(platform.id);

    if (connectedAccount) {
      return (
        <button
          key={platform.id}
          onClick={() => !isDisabled && handlePlatformToggle(platform.id)}
          disabled={isDisabled}
          className={`flex flex-col items-center justify-center gap-1 sm:gap-2 p-2 sm:p-3 h-24 w-full rounded-lg border ${
            isDisabled
              ? 'border-gray-300 bg-gray-50 cursor-not-allowed opacity-50'
              : isSelected
                ? 'border-primary-600 bg-primary-50'
                : 'border-black hover:border-primary-600'
          }`}
        >
          <SocialProfilePicture
            pictureUrl={connectedAccount.picture_url}
            username={connectedAccount.username}
            platform={platform.id}
            size="md"
          />
          <span className={`text-[10px] w-full text-center line-clamp-2 overflow-hidden whitespace-normal break-words ${
            isDisabled ? 'text-gray-400' : 'text-gray-700'
          }`}>@{connectedAccount.username}</span>
        </button>
      );
    }

    return (
      <button
        key={platform.id}
        onClick={() => !isDisabled && handlePlatformToggle(platform.id)}
        disabled={isDisabled}
        className={`flex flex-col items-center justify-center gap-1 sm:gap-2 p-2 sm:p-3 h-24 w-full rounded-lg border ${
          isDisabled
            ? 'border-gray-300 bg-gray-50 cursor-not-allowed opacity-50'
            : isSelected
              ? 'border-primary-600 bg-primary-50'
              : 'border-black hover:border-primary-600'
        }`}
      >
        <SocialIcon platform={platform.id} size={24} />
        <span className={`text-[10px] w-full text-center line-clamp-2 overflow-hidden whitespace-normal break-words ${
          isDisabled ? 'text-gray-400' : 'text-gray-700'
        }`}>{platform.name}</span>
      </button>
    );
  };

  return (
    <div className="bg-white rounded-xl border border-black shadow-lg p-4 sm:p-6 space-y-4">
      <div className="flex items-center gap-3 mb-4">
        <Share2 className="w-5 h-5 text-gray-400" />
        <h2 className="text-lg font-semibold text-gray-900">{t('cards.brandsAndPlatforms.title', 'Marques & Plateformes')}</h2>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('cards.brands.title', 'Brand')}</label>
        <BrandDropdown
          brands={brands}
          selectedBrandId={selectedBrandId}
          onBrandSelect={onBrandSelect}
          isLoading={isLoadingBrands}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('postContent.formats', 'Format')}</label>
        <div className="flex gap-2">
          {/* Regular Format Button */}
          <button
            onClick={() => onFormatChange?.('regular')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors duration-150 ${
              format === 'regular'
                ? 'bg-primary-50 border-primary-600 text-primary-700'
                : 'bg-white border-black text-gray-700 hover:bg-gray-50 hover:border-gray-400'
            }`}
          >
            <span className="text-lg">📸</span>
            <div className="text-left">
              <div>{t('postContent.regularFormat', 'Regular (Horizontal)').split(' (')[0]}</div>
              <div className="text-xs opacity-75">({t('postContent.regularFormat', 'Regular (Horizontal)').split(' (')[1]}</div>
            </div>
          </button>
          {/* Reel Format Button */}
          <button
            onClick={() => onFormatChange?.('reel')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors duration-150 ${
              format === 'reel'
                ? 'bg-primary-50 border-primary-600 text-primary-700'
                : 'bg-white border-black text-gray-700 hover:bg-gray-50 hover:border-gray-400'
            }`}
          >
            <span className="text-lg">📱</span>
            <div className="text-left">
              <div>{t('postContent.reelFormat', 'Reel (Vertical)').split(' (')[0]}</div>
              <div className="text-xs opacity-75">({t('postContent.reelFormat', 'Reel (Vertical)').split(' (')[1]}</div>
            </div>
          </button>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('cards.platforms.sectionTitle', 'Plateformes')}</label>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
          {displayPlatforms.map(platform => renderPlatformButton(platform))}
        </div>
      </div>
    </div>
  );
}; 